import { z } from 'zod';
import { GameSettingsRoutingSchema } from './game-setting-rules.js';
import { advancedGameShape, advancedGameDefaults } from './server-properties.js';

export const RegistryIdSchema = z
  .string()
  .max(200)
  .regex(/^[a-z0-9_.-]+:[a-z0-9_./-]+$/);
const text = z
  .string()
  .max(200)
  .refine((v) => !/[\r\n\0]/.test(v), '改行・NUL は指定できません');
const WritableGameSettingsSchema = z
  .object({
    gamemode: z.enum(['survival', 'creative', 'adventure', 'spectator']),
    difficulty: z.enum(['peaceful', 'easy', 'normal', 'hard']),
    hardcore: z.boolean(),
    pvp: z.boolean(),
    'max-players': z.number().int().min(1).max(10000),
    'spawn-protection': z.number().int().min(0).max(29999984),
    'allow-flight': z.boolean(),
    motd: text,
    ...advancedGameShape,
    'allow-end': z.boolean().optional(),
  })
  .strict();
export const GameSettingsSchema = WritableGameSettingsSchema.extend({
  pvp: z.boolean().nullable(),
  'allow-nether': z.boolean().nullable(),
  'spawn-monsters': z.boolean().nullable(),
  'enable-command-block': z.boolean().nullable(),
  routing: GameSettingsRoutingSchema.optional(),
  settingMessages: z.record(z.string(), z.string()).optional(),
  dimensionValues: z.record(z.string(), z.record(z.string(), z.boolean())).optional(),
  readOnlyProperties: z.record(z.string(), z.string()).optional(),
});
export const GameSettingsPatchSchema = WritableGameSettingsSchema.partial().refine(
  (v) => Object.keys(v).length > 0,
  '変更するゲーム設定が必要です',
);
export type GameSettings = z.infer<typeof GameSettingsSchema>;
export type GameSettingsPatch = z.infer<typeof GameSettingsPatchSchema>;
/** 変更できるゲーム設定の項目。routing・settingMessages などの表示用の項目は含まない */
export const writableGameSettingKeys = Object.keys(WritableGameSettingsSchema.shape) as (keyof GameSettingsPatch)[];
/**
 * 画面の値と現在値の差分。変更できる項目だけを比べる。
 * 表示用の項目はオブジェクトなので、現在値を読み直すたびに別物になり、差分に混ざると変更を確認できなくなっていた
 */
export function gameSettingsPatch(value: GameSettings, current: GameSettings): Record<string, unknown> {
  return Object.fromEntries(
    writableGameSettingKeys.filter((key) => !Object.is(value[key], current[key])).map((key) => [key, value[key]]),
  );
}
export const defaultGameSettings: GameSettings = {
  gamemode: 'survival',
  difficulty: 'easy',
  hardcore: false,
  pvp: true,
  'max-players': 20,
  'spawn-protection': 16,
  'allow-flight': false,
  motd: 'A Minecraft Server',
  ...advancedGameDefaults,
};

const layer = z.object({ block: RegistryIdSchema, height: z.number().int().min(1).max(384) }).strict();
export const FlatSettingsSchema = z
  .object({
    layers: z
      .array(layer)
      .min(1)
      .max(384)
      .refine((layers) => layers.reduce((sum, l) => sum + l.height, 0) <= 384, '層の合計は 384 以下にしてください'),
    biome: RegistryIdSchema,
    structures: z.boolean(),
    features: z.boolean().default(false),
    lakes: z.boolean().default(false),
    structureOverrides: z.array(RegistryIdSchema).max(100).optional(),
  })
  .strict();
export type FlatSettings = z.infer<typeof FlatSettingsSchema>;

// Mojang 1.21.10 client.jar: data/minecraft/worldgen/flat_level_generator_preset/*.json。
// https://piston-meta.mojang.com/mc/game/version_manifest_v2.json から配布物を取得して確認。
export const flatPresets: Record<string, { label: string; settings: FlatSettings }> = {
  classic_flat: {
    label: 'クラシック',
    settings: {
      biome: 'minecraft:plains',
      layers: [
        { block: 'minecraft:bedrock', height: 1 },
        { block: 'minecraft:dirt', height: 2 },
        { block: 'minecraft:grass_block', height: 1 },
      ],
      structures: true,
      features: false,
      lakes: false,
      structureOverrides: ['minecraft:villages'],
    },
  },
  tunnelers_dream: {
    label: 'トンネル掘りの夢',
    settings: {
      biome: 'minecraft:windswept_hills',
      layers: [
        { block: 'minecraft:bedrock', height: 1 },
        { block: 'minecraft:stone', height: 230 },
        { block: 'minecraft:dirt', height: 5 },
        { block: 'minecraft:grass_block', height: 1 },
      ],
      structures: true,
      features: true,
      lakes: false,
      structureOverrides: ['minecraft:mineshafts', 'minecraft:strongholds'],
    },
  },
  water_world: {
    label: '水の世界',
    settings: {
      biome: 'minecraft:deep_ocean',
      layers: [
        { block: 'minecraft:bedrock', height: 1 },
        { block: 'minecraft:deepslate', height: 64 },
        { block: 'minecraft:stone', height: 5 },
        { block: 'minecraft:dirt', height: 5 },
        { block: 'minecraft:gravel', height: 5 },
        { block: 'minecraft:water', height: 90 },
      ],
      structures: true,
      features: false,
      lakes: false,
      structureOverrides: ['minecraft:ocean_ruins', 'minecraft:shipwrecks', 'minecraft:ocean_monuments'],
    },
  },
  the_void: {
    label: '何もない',
    settings: {
      biome: 'minecraft:the_void',
      layers: [{ block: 'minecraft:air', height: 1 }],
      structures: false,
      features: true,
      lakes: false,
      structureOverrides: [],
    },
  },
};

const common = {
  seed: text.optional(),
  generateStructures: z.boolean().default(true),
  // Existing custom presets may carry JSON settings. Keep them when regenerating without a saved replacement.
  generatorSettings: z
    .string()
    .max(1024 * 1024)
    .optional(),
};
export const WorldGenerationSchema = z.discriminatedUnion('worldType', [
  z.object({ ...common, worldType: z.literal('normal') }).strict(),
  z.object({ ...common, worldType: z.literal('large_biomes') }).strict(),
  z.object({ ...common, worldType: z.literal('amplified') }).strict(),
  z.object({ ...common, worldType: z.literal('flat'), flat: FlatSettingsSchema }).strict(),
  z.object({ ...common, worldType: z.literal('single_biome'), biome: RegistryIdSchema }).strict(),
  z.object({ ...common, worldType: z.literal('custom'), customId: RegistryIdSchema }).strict(),
]);
export type WorldGeneration = z.infer<typeof WorldGenerationSchema>;

/** 値は未エスケープのままサービス層を通し、ファイルを書く境界だけでエスケープする。 */
export function escapePropertyValue(value: string): string {
  return value
    .replaceAll('\\', '\\\\')
    .replaceAll(':', '\\:')
    .replaceAll('=', '\\=')
    .replaceAll('\r', '\\r')
    .replaceAll('\n', '\\n')
    .replaceAll('\t', '\\t')
    .replace(/^ /, '\\ ')
    .replace(/[^\x20-\x7e]/g, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`);
}
export function gameSettingsProperties(settings: GameSettingsPatch): Record<string, string> {
  return Object.fromEntries(
    Object.entries(GameSettingsPatchSchema.parse(settings))
      .filter(([k]) => k !== 'allow-end')
      .map(([k, v]) => [k, String(v)]),
  );
}
export function gameSettingsFromProperties(properties: Record<string, string>, includeReadOnly = false): GameSettings {
  const values: Record<string, unknown> = { ...defaultGameSettings };
  for (const [key, value] of Object.entries(properties)) {
    if (!Object.hasOwn(defaultGameSettings, key)) continue;
    const def = defaultGameSettings[key as keyof GameSettings];
    values[key] = typeof def === 'boolean' ? value === 'true' : typeof def === 'number' ? Number(value) : value;
  }
  if (includeReadOnly)
    values.readOnlyProperties = Object.fromEntries(
      Object.entries(properties)
        .filter(([key]) => !Object.hasOwn(defaultGameSettings, key))
        .map(([key, value]) => [key, /password|secret|token/i.test(key) ? '（非表示）' : value]),
    );
  return GameSettingsSchema.parse(values);
}
export function worldGenerationProperties(value: WorldGeneration): Record<string, string> {
  const settings = WorldGenerationSchema.parse(value);
  const type =
    settings.worldType === 'custom'
      ? settings.customId
      : `minecraft:${settings.worldType === 'single_biome' ? 'single_biome_surface' : settings.worldType}`;
  const result: Record<string, string> = {
    'level-type': type,
    'level-seed': settings.seed ?? '',
    'generate-structures': String(settings.generateStructures),
    'generator-settings': settings.generatorSettings ?? '{}',
  };
  if (settings.worldType === 'single_biome') result['initial-enabled-packs'] = 'vanilla,file/mcpanel-single-biome';
  if (settings.worldType === 'flat') {
    const flat = settings.flat;
    result['generator-settings'] = JSON.stringify({
      layers: flat.layers,
      biome: flat.biome,
      features: flat.features,
      lakes: flat.lakes,
      ...(!settings.generateStructures || !flat.structures
        ? { structure_overrides: [] }
        : flat.structureOverrides
          ? { structure_overrides: flat.structureOverrides }
          : {}),
    });
  }
  return result;
}
export function initialWorldProperties(
  template: Record<string, string>,
  defaults?: GameSettingsPatch,
  game?: GameSettingsPatch,
  world?: WorldGeneration,
): Record<string, string> {
  return {
    ...template,
    ...(defaults ? gameSettingsProperties(defaults) : {}),
    ...(game ? gameSettingsProperties(game) : {}),
    ...(world ? worldGenerationProperties(world) : {}),
  };
}

// 作者の説明: https://moderner.nostalgica.net/docs/frequently-asked-questions
// Modrinth の project ID は https://api.modrinth.com/v2/project/moderner-beta で確認。
// Modern Beta author repository: src/main/generated/data/modern_beta/worldgen/world_preset/modern_beta.json
// https://github.com/b3spectacled/modern-beta-fabric
export const customWorldTypes = [
  { projects: ['modern-beta', 'GPz09hcB'], id: 'modern_beta:modern_beta', label: 'Modern Beta' },
  { projects: ['moderner-beta', 'xkrdwmh2'], id: 'moderner_beta:moderner_beta', label: 'Moderner Beta' },
];
export function customWorldCandidates(
  mods: Array<{ project?: string; projectId?: string; slug?: string; source?: string }>,
) {
  return customWorldTypes.filter((candidate) =>
    mods.some(
      (mod) =>
        (!mod.source || mod.source === 'modrinth') &&
        [mod.project, mod.projectId, mod.slug].some((id) => id !== undefined && candidate.projects.includes(id)),
    ),
  );
}
export const WorldGenerationOptionsSchema = z
  .object({
    customWorldTypes: z.array(z.object({ id: RegistryIdSchema, label: z.string() })),
  })
  .strict();
export type WorldGenerationOptions = z.infer<typeof WorldGenerationOptionsSchema>;

export function generationFromProperties(properties: Record<string, string>): WorldGeneration {
  const common = {
    seed: properties['level-seed'] ?? '',
    generateStructures: properties['generate-structures'] !== 'false',
    generatorSettings: properties['generator-settings'] ?? '{}',
  };
  const type = (properties['level-type'] ?? 'minecraft:normal').replace(/^minecraft:/, '');
  if (type === 'flat') {
    const flat = JSON.parse(properties['generator-settings'] || '{}') as Record<string, unknown>;
    return WorldGenerationSchema.parse({
      ...common,
      worldType: 'flat',
      flat: {
        layers: flat.layers ?? flatPresets.classic_flat!.settings.layers,
        biome: flat.biome ?? 'minecraft:plains',
        features: flat.features ?? false,
        lakes: flat.lakes ?? false,
        structures: !Array.isArray(flat.structure_overrides) || flat.structure_overrides.length > 0,
        structureOverrides: flat.structure_overrides,
      },
    });
  }
  if (['normal', 'large_biomes', 'amplified'].includes(type))
    return WorldGenerationSchema.parse({ ...common, worldType: type });
  if (type === 'single_biome_surface') return { ...common, worldType: 'single_biome', biome: 'minecraft:plains' };
  return WorldGenerationSchema.parse({ ...common, worldType: 'custom', customId: properties['level-type'] });
}

// Author sources; these change normal generation and do not require an invented level-type ID.
// https://github.com/Glitchfiend/BiomesOPlenty
// https://github.com/Stardust-Labs-MC/Terralith
export const generationModifiers = [
  {
    projects: ['biomes-o-plenty', 'HXF82T3G'],
    curseforge: ['220318'],
    label: "Biomes O' Plenty",
    message: '通常のワールドにバイオームを追加します',
  },
  {
    projects: ['terralith', '8oi3bsk5'],
    curseforge: ['513688'],
    label: 'Terralith',
    message: '通常のワールドの地形とバイオームを変更します',
  },
];
export function installedGenerationModifiers(
  mods: Array<{ projectId?: string; slug?: string; source?: string }>,
  datapacks: string[],
) {
  const normal = generationModifiers.filter(
    (candidate) =>
      mods.some((mod) =>
        (mod.source === 'curseforge' ? candidate.curseforge : candidate.projects).some(
          (id) => id === mod.projectId || id === mod.slug,
        ),
      ) ||
      datapacks.some((name) =>
        candidate.projects.some((id) =>
          name
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')
            .startsWith(id.replace(/[^a-z0-9]/g, '')),
        ),
      ),
  );
  return [
    ...normal.map(({ label, message }) => ({ label, message })),
    ...customWorldCandidates(mods).map(({ label }) => ({
      label,
      message: '独自の生成方法を追加します。生成方法の ID を選択してください',
    })),
  ];
}
